import React, {useCallback, useContext, useRef, useState} from 'react';
import {TextField} from "bonprix-design-system";
import {articleOverviewStaticContext} from '../../ArticleOverviewStatic.jsx';
import Slider from './Slider.jsx';

function Range({filterOptions, filterOptionChange, identifier, trackingId, unit, dropdown, mobileSubmitButton, range}) {
    const {texts} = useContext(articleOverviewStaticContext);

    const rangeFilterRef = useRef(null);

    const [invalid, setInvalid] = useState(false);
    const [noHits, setNoHits] = useState(false);

    const hasValidRange = range != null && range.min != null && range.max != null;

    // original bounds
    const availableRange = useRef({
        min: hasValidRange ? range.min : 0,
        max: hasValidRange ? range.max : 0,
    });

    // committed numeric range
    const [selectedRange, setSelectedRange] = useState(() => {
        const minVal = filterOptions?.[0]?.value;
        const maxVal = filterOptions?.[1]?.value;
        const min = minVal != null ? Number(minVal) : (hasValidRange ? Number(range.min) : null);
        const max = maxVal != null ? Number(maxVal) : (hasValidRange ? Number(range.max) : null);
        return [min, max];
    });

    // draft input values (strings)
    const [draft, setDraft] = useState(() => {
        const minVal = filterOptions?.[0]?.value;
        const maxVal = filterOptions?.[1]?.value;
        const min = minVal ?? (hasValidRange ? range.min : '');
        const max = maxVal ?? (hasValidRange ? range.max : '');
        return { min: String(min), max: String(max) };
    });

    const draftRef = useRef(draft);
    draftRef.current = draft;

    const rangeRef = useRef(selectedRange);
    rangeRef.current = selectedRange;

    const checkRangeFilterValues = useCallback(async (minVal, maxVal) => {
        const inputValid = minVal <= maxVal;

        const value = `${minVal}<${maxVal}`;
        const hits = await filterOptionChange(identifier, trackingId, [value], true);
        const hasHits = hits !== 0;

        if (inputValid && hasHits) {
            await filterOptionChange(identifier, trackingId, [value], false);
        }

        setInvalid(!inputValid);
        setNoHits(!hasHits);
    }, [dropdown, mobileSubmitButton, filterOptionChange, identifier, trackingId, hasValidRange]);

    // Called during slider drag (only update visual state)
    const handleRangeChange = useCallback(([a, b]) => {
        const min = Math.min(a, b);
        const max = Math.max(a, b);

        setSelectedRange([min, max]);
        setDraft({ min: String(min), max: String(max) });
    }, []);

    // Called when slider drag ends
    const handleRangeCommit = useCallback(async ([a, b]) => {
        const min = Math.min(a, b);
        const max = Math.max(a, b);

        setSelectedRange([min, max]);
        setDraft({ min: String(min), max: String(max) });

        await checkRangeFilterValues(min, max);
    }, [checkRangeFilterValues]);

    const handleMinChange = useCallback((valueOrEvent) => {
        const value = typeof valueOrEvent === 'object' ? valueOrEvent?.target?.value : valueOrEvent;
        if (value === "" || /^\d+$/.test(value)) {
            setDraft(d => ({ ...d, min: value }));
        }
    }, []);

    const handleMaxChange = useCallback((valueOrEvent) => {
        const value = typeof valueOrEvent === 'object' ? valueOrEvent?.target?.value : valueOrEvent;
        if (value === "" || /^\d+$/.test(value)) {
            setDraft(d => ({ ...d, max: value }));
        }
    }, []);

    const commitDraft = useCallback(async () => {
        const currentDraft = draftRef.current;
        const currentRange = rangeRef.current;
        const { min: domainMin, max: domainMax } = availableRange.current;

        // Empty fields are not valid
        if (currentDraft.min === '' || currentDraft.max === '') {
            return;
        }

        const min = Number(currentDraft.min);
        const max = Number(currentDraft.max);

        const currentMin = currentRange[0];
        const currentMax = currentRange[1];
        if (currentMin != null && currentMax != null && min === currentMin && max === currentMax) {
            return;
        }

        const isBasicInvalid = Number.isNaN(min) || Number.isNaN(max) || min > max;

        const isDomainInvalid = hasValidRange && (min < domainMin || max > domainMax);

        // If invalid rollback
        if (isBasicInvalid || isDomainInvalid) {
            setDraft({
                min: currentRange[0] != null ? String(currentRange[0]) : '',
                max: currentRange[1] != null ? String(currentRange[1]) : '',
            });
            return;
        }

        setSelectedRange([min, max]);
        await checkRangeFilterValues(min, max);
    }, [checkRangeFilterValues, hasValidRange]);

    const handleKeyDown = useCallback((e) => {
        const key = e?.key || e?.which || e?.keyCode;
        const isEnter = key === 'Enter' || key === 13 || key === 'Done' || key === 'Go';
        if (isEnter) {
            e.preventDefault?.();
            commitDraft();
        }
    }, [commitDraft]);

    const invalidTextHint = () => {
        if (invalid) {
            return texts.invalid_range_input;
        }
        if (noHits) {
            return texts.no_hits_found;
        }
        return '';
    };

    return (
        <div className="filter__range" ref={rangeFilterRef}>
            <div className="filter__range-content">
                {(invalid || noHits) && (
                    <div className="filter__range-no-hits-text">
                        <span> {invalidTextHint()} </span>
                    </div>
                )}
                <span className="filter__range-inputs flex gap-2 items-center">
                    <TextField
                        value={draft.min}
                        label={`${texts.filter_range_from} (${unit})`}
                        name={"minValue"}
                        inputMode="numeric"
                        onChange={handleMinChange}
                        onBlur={commitDraft}
                        onKeyDown={handleKeyDown}
                        error={invalid || noHits}
                        errorText={invalidTextHint()}
                        hideClear={true}
                        type="text"
                    />
                    <span className="flex items-center pt-5">-</span>
                    <TextField
                        value={draft.max}
                        label={`${texts.filter_range_to} (${unit})`}
                        name={"maxValue"}
                        inputMode="numeric"
                        onChange={handleMaxChange}
                        onBlur={commitDraft}
                        onKeyDown={handleKeyDown}
                        error={invalid || noHits}
                        errorText={invalidTextHint()}
                        hideClear={true}
                    />
                </span>
                {hasValidRange && (
                    <div className="pt-5 pb-6">
                        <Slider
                            min={availableRange.current.min}
                            max={availableRange.current.max}
                            step={1}
                            value={selectedRange}
                            onValueChange={handleRangeChange}
                            onValueCommit={handleRangeCommit}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}


export default React.memo(Range);
